import { NavLink } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'
import { useTenant } from '@/contexts/TenantContext'
import { 
  Home, 
  Upload, 
  BarChart3, 
  Users, 
  Settings, 
  FileText,
  Globe,
  Shield,
  Palette,
  Database,
  Activity,
  Zap,
  Cpu,
  Rocket,
  Music,
  Sparkles,
  Mic,
  Guitar
} from 'lucide-react'
import { staggerContainer, staggerItem } from '../../lib/animations'

const Sidebar: React.FC = () => {
  const { user } = useAuth()
  const { isClient, isCreator, isInternal } = useTenant()

  const getMenuItems = () => {
    if (isClient) {
      return [
        { label: 'Dashboard', href: '/client', icon: Home },
        { label: 'Content Upload', href: '/client/upload', icon: Upload },
        { label: 'Analytics', href: '/client/analytics', icon: BarChart3 },
        { label: 'API Docs', href: '/client/api', icon: FileText },
        { label: 'Webhooks', href: '/client/webhooks', icon: Globe },
        { label: 'Settings', href: '/client/settings', icon: Settings },
      ]
    } else if (isCreator) {
      return [
        { label: 'Studio', href: '/creator', icon: Home },
        { label: 'Upload', href: '/creator/upload', icon: Upload },
        { label: 'Portfolio', href: '/creator/portfolio', icon: Palette },
        { label: 'Analytics', href: '/creator/analytics', icon: BarChart3 },
        { label: 'NFTs', href: '/creator/nfts', icon: Shield },
        { label: 'Settings', href: '/creator/settings', icon: Settings },
      ]
    } else if (isInternal) {
      return [
        { label: 'Overview', href: '/admin', icon: Home },
        { label: 'Tenants', href: '/admin/tenants', icon: Users },
        { label: 'Content', href: '/admin/content', icon: Database },
        { label: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
        { label: 'System', href: '/admin/system', icon: Activity },
        { label: 'Settings', href: '/admin/settings', icon: Settings },
      ]
    }
    return []
  }

  const menuItems = getMenuItems()
  
  const getPortalType = () => {
    if (isClient) return { title: 'ENTERPRISE PORTAL', icon: Cpu, color: 'text-stellar-blue' };
    if (isCreator) return { title: 'CREATIVE HUB', icon: Music, color: 'text-stellar-pink' };
    if (isInternal) return { title: 'ADMIN PANEL', icon: Shield, color: 'text-stellar-green' };
    return { title: 'COSMK VAULT', icon: Rocket, color: 'text-stellar-blue' };
  }

  const portalInfo = getPortalType()
  const PortalIcon = portalInfo.icon

  return (
    <motion.div 
      className="w-64 cosmic-glass h-screen flex flex-col relative"
      initial={{ x: -300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      {/* Top border accent */}
      <div className="absolute top-0 left-0 w-full h-1 bg-stellar-gradient" />
      
      {/* Header */}
      <motion.div 
        className="p-6 border-b border-stellar-blue/20"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <div className="flex items-center gap-3 mb-2">
          <motion.div
            className={`text-2xl ${portalInfo.color}`}
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          >
            <PortalIcon />
          </motion.div>
          <div>
            <h1 className="text-xl font-display font-semibold text-white glow-text">
              COSMK VAULT
            </h1>
            <p className={`text-xs font-mono uppercase tracking-wider ${portalInfo.color}`}>
              {portalInfo.title}
            </p>
          </div>
        </div>
        
        {/* System Status */}
        <div className="mt-4 p-3 bg-cosmic-dark/50 rounded-lg border border-stellar-blue/20">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono text-stellar-cyan uppercase tracking-wider">
              Hub Status
            </span>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-stellar-green rounded-full animate-pulse" />
              <span className="text-xs font-mono text-stellar-green uppercase">
                Live
              </span>
            </div>
          </div>
        </div>
      </motion.div>
      
      {/* Navigation */}
      <motion.nav 
        className="flex-1 px-3 py-4"
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        <div className="space-y-2">
          {menuItems.map((item, index) => {
            const Icon = item.icon
            return (
              <motion.div
                key={item.href}
                variants={staggerItem}
                custom={index}
              >
                <NavLink
                  to={item.href}
                  className={({ isActive }) =>
                    `flex items-center px-4 py-3 text-sm font-mono uppercase tracking-wider rounded-lg transition-all duration-300 group relative overflow-hidden ${
                      isActive
                        ? 'bg-stellar-blue/20 text-stellar-blue border border-stellar-blue/50 glow-text'
                        : 'text-stellar-cyan hover:bg-stellar-blue/10 hover:text-stellar-blue hover:border-stellar-blue/30 border border-transparent'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {/* Animated background sweep */}
                      <motion.div
                        className="absolute inset-0 bg-stellar-blue opacity-0 group-hover:opacity-10"
                        initial={{ x: '-100%' }}
                        whileHover={{ x: '100%' }}
                        transition={{ duration: 0.5 }}
                      />
                      
                      <motion.div
                        className="text-lg mr-3"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Icon />
                      </motion.div>
                      
                      <span className="relative z-10">{item.label}</span>
                      
                      {/* Active indicator */}
                      {isActive && (
                        <motion.div
                          className="absolute right-2 w-2 h-2 bg-stellar-blue rounded-full"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              </motion.div>
            )
          })}
        </div>
      </motion.nav>

      {/* User Profile */}
      <motion.div 
        className="p-4 border-t border-stellar-blue/20"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
      >
        <div className="cosmic-glass rounded-lg p-3">
          <div className="flex items-center">
            <motion.div 
              className="w-10 h-10 bg-stellar-gradient rounded-full flex items-center justify-center border-2 border-stellar-blue/50"
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.2 }}
            >
              <span className="text-white text-sm font-display font-medium">
                {user?.email?.charAt(0).toUpperCase()}
              </span>
            </motion.div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-mono text-white truncate">
                {user?.email}
              </p>
              <p className="text-xs font-mono text-stellar-cyan uppercase tracking-wider">
                {user?.role}
              </p>
            </div>
            <motion.div
              className="w-2 h-2 bg-stellar-green rounded-full"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default Sidebar 